import { getWmPoiId, getAcctId, getDeviceId } from '@utils/ids';
import { getCookie } from '@utiljs/cookie';
import { isHitGray, ICommoneHornGrayConfig } from '@utils/commonHornGrayUtils'; // eslint-disable-line import/no-cycle

import { getPoiEnabledConnectedPrinters } from '@utils/print/printer';

import { LLog } from '@bfe/logan-rtl-js-web';
import Horn from '@utils/horn'; // eslint-disable-line import/no-cycle
import { ENV, getEnv, isInApp } from '@utils/getEnv';

const APP_KEY = 'waimai_e_fe_core';

interface ILLogConfigItem extends ICommoneHornGrayConfig {
    /**当前业务所属页面名称 */
    pageName: string[];
    appKey: string;
}

enum REPORT_TAG_TYPE {
    DOM_CLICK = 'domClick',
    MODULE_VIEW = 'moduleView',
    PAGE_JUMP = 'pageJump',
    REDUX_ACTION = 'reduxAction',
    POST_MESSAGE = 'postMessage',
    BRIDGE_PLATFORM = 'bridgePlatform',
    REQUEST = 'request',
    TOAST = 'toast',
}

type IReportType = string | REPORT_TAG_TYPE;

const currentEnv = getEnv();
/**
 * llog 实时日志 https://km.sankuai.com/page/1236122753 https://km.sankuai.com/page/1274159782
 * 查看日志地址 https://logan.mws.sankuai.com/rtl/web?tab=advancedQuery&categoryId=18&userIDs=&levels=&tags=&keywords=&dsl=&current=1&pageSize=20&timeType=minute&beginTime=1645689361677&endTime=1645691161677&isAsc=-1
 */
const SHARED_DATA_CUSTOM = {
    isInApp,
    ua: navigator.userAgent,
    httpType: window.location.protocol.replace(/:$/g, ''),
};
const userInfo = {
    acctId: getAcctId(),
    wmPoiId: getWmPoiId(),
    deviceUuid: getDeviceId(),
    //https://km.sankuai.com/page/119677578#id-3.%E5%90%8D%E8%AF%8D%E8%A7%A3%E9%87%8A%EF%BC%9A
    unionId:
        getCookie('uuid') || getCookie('unionid') || getCookie('dpid') || getCookie('_lxsdk_cuid'),
};
const reportBasicInfo = {
    ...SHARED_DATA_CUSTOM,
    userInfo,
    printers: SHARED_DATA_CUSTOM.isInApp ? getPoiEnabledConnectedPrinters(getWmPoiId()) : [],
};
// 页面是否上报标识
let ifLLogReport = false;

/** 当前初始化的页面名称，用作调用上报时所属页面信息标识 */
let currentPage: string = '';

const LLogStart = () => {
    const { unionId } = userInfo;
    // userConfig: JSON 对象，必填。需包含以下内容：
    //              - appKey: string 必填 区分业务
    //              - topic: string  必填 消息的目标 topic
    //              - userID: string 可选 后台可筛选用户ID
    //
    // devMode: boolean，必填。是否是开发模式，默认为 false。控制成功率指标上传环境以及远程开关环境 使远端上报灰度可生效，不控制日志环境。目前，日志环境只有线
    try {
        if (LLog) {
            LLog.start(
                {
                    topic:
                        currentEnv === ENV.TEST
                            ? 'app.waimaie.fe.realtime.test'
                            : 'app.waimaie.fe.realtime',
                    appKey: APP_KEY,
                    userID: `${unionId}`,
                },
                currentEnv === ENV.TEST,
            );
            console.log('start obj devMode', currentEnv === ENV.TEST);
        }
    } catch (e) {
        // Ooops
        console.log('llog start error', e);
    }
};
/**
 * 记录一条 info 级别的日志
 *
 * @param data 事件数据
 * @param reportType 事件类型 除业务流程事件以外，其他以下类型为准 'domClick','moduleView','pageJump','reduxAction','postMessage','request'
 */
const info = (data: any, reportType: IReportType) => {
    try {
        if (LLog && ifLLogReport) {
            // console.log('llog info', currentPage, reportType, JSON.stringify({ data }));
            LLog.info(JSON.stringify({ data }), currentPage, reportType);
        }
    } catch (e) {
        // console.log('llog catch', e);
    }
};
/**
 * 记录一条 warn 级别的日志
 *
 * @param data 事件数据
 * @param reportType 上报事件类型 除业务流程事件以外，其他以下类型为准 'domClick','moduleView','pageJump','reduxAction','postMessage','request'
 */
const warn = (data: any, reportType: IReportType) => {
    try {
        if (LLog && ifLLogReport) {
            LLog.warn(JSON.stringify({ data }), currentPage, reportType);
        }
    } catch (e) {
        // Ooops
    }
};
/**
 * 记录一条 error 级别的日志
 *
 * @param data 事件数据
 * @param reportType 上报事件类型 除业务流程事件以外，其他以下类型为准 'domClick','moduleView','pageJump','reduxAction','postMessage','request'
 */
const error = (data: any, reportType: IReportType) => {
    try {
        if (LLog && ifLLogReport) {
            LLog.error(JSON.stringify({ data }), currentPage, reportType);
        }
    } catch (e) {
        // Ooops
    }
};
/**
 * 获取horn配置
 */
const getLLogConfigFromHorn = () => {
    try {
        return Horn.getConfigByFieldName<ILLogConfigItem[]>(Horn.CONFIG_FIELD.LLOG_CONFIG);
    } catch (e) {
        // console.log('[Logan Horn] 白名单检查失败'); // eslint-disable-line no-console
        return null;
    }
};
/**
 * 是否命中horn配置
 * @param currentPageName
 */
const isHitLLogHornConfig = (currentPageName: string): boolean => {
    const LLogHornConfig = getLLogConfigFromHorn();
    if (Array.isArray(LLogHornConfig)) {
        let result = false;
        try {
            LLogHornConfig.forEach(sampleConfig => {
                const { appKey, pageName, openList, closeList, percent } = sampleConfig;
                if (
                    pageName.includes(currentPageName) &&
                    appKey === APP_KEY &&
                    isHitGray({ openList, closeList, percent })
                ) {
                    result = true;
                } else {
                    result = false;
                }
            });
        } catch (event) {
            if (window.Owl && window.Owl.addError) {
                window.Owl.addError(event);
            }
        }
        return result;
    } else {
        return false;
    }
};
/**
 * 通过horn或push，初始化LLog.start，该方法需放置于所有LLog.info等上报之前调用，否则可能会导致LLog.info无法上报
 * @param pageName 当前页面名称，需要同horn中的openPages配置保持一致
 * @param initType LLog开启初始化类型 : "horn", "push"
 * @param pushData 若初始化来源于Push, 则需传入pushData
 */
const initLLog = (pageName: string) => {
    if (!LLog) {
        return;
    }
    if (isHitLLogHornConfig(pageName)) {
        currentPage = pageName;
        ifLLogReport = true;
        LLogStart();
        //开启上报初始化之后，上报一条门店id及设备信息，包含unionId的关联
        info(reportBasicInfo, 'waimaieBasicInfo');
    } else {
        ifLLogReport = false;
    }
};

export { info, error, warn, initLLog, REPORT_TAG_TYPE };
